import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export function Login() {
  const [email, setEmail] = useState("admin@thegoodguys.app");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return <AuthShell title="Welcome back" subtitle="Sign in to your Good Guys console">
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <Field label="Work email" id="email">
        <Input data-testid="input-login-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-slate-950 border-slate-800 h-11" />
      </Field>
      <Field label="Password" id="password">
        <Input data-testid="input-login-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-slate-950 border-slate-800 h-11" />
      </Field>
      <Button data-testid="btn-login-submit" type="submit" disabled={loading} className="w-full bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold h-11">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in →"}
      </Button>
      <p className="text-sm text-slate-400 text-center">
        No account? <Link to="/register" className="text-cyan-signal hover:underline">Create one free</Link>
      </p>
    </form>
  </AuthShell>;
}

export function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, ref);
      toast.success(ref ? "Account created — 600 credits added (500 + 100 referral)" : "Account created — 500 credits added");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return <AuthShell title="Create your account" subtitle={ref ? "500 free + 100 referral bonus" : "500 free credits · no card required"}>
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      {ref && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 text-sm text-cyan-signal" data-testid="register-referral-banner">
          Referral applied · +100 bonus credits
        </div>
      )}
      <Field label="Name" id="name">
        <Input data-testid="input-register-name" id="name" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-950 border-slate-800 h-11" />
      </Field>
      <Field label="Work email" id="email">
        <Input data-testid="input-register-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-slate-950 border-slate-800 h-11" />
      </Field>
      <Field label="Password (min 6)" id="password">
        <Input data-testid="input-register-password" id="password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-slate-950 border-slate-800 h-11" />
      </Field>
      <Button data-testid="btn-register-submit" type="submit" disabled={loading} className="w-full bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold h-11">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account →"}
      </Button>
      <p className="text-sm text-slate-400 text-center">
        Have an account? <Link to="/login" className="text-cyan-signal hover:underline">Sign in</Link>
      </p>
    </form>
  </AuthShell>;
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-mono uppercase tracking-widest text-slate-400">{label}</Label>
      {children}
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 items-center justify-center mb-4 shadow-[0_0_30px_-4px_rgba(0,240,255,0.6)]">
            <Shield className="w-6 h-6 text-slate-950" strokeWidth={2.75} fill="currentColor" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-slate-400">{subtitle}</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-xl p-6 sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}
